import React from "react";
import { createRoot } from "react-dom/client";

import {
  Button,
  Checkbox,
  ConfigProvider,
  Divider,
  Dropdown,
  Input,
  Layout,
  Segmented,
  Tooltip,
  theme as antdTheme,
} from "antd";
import "antd/dist/reset.css";
import {
  AppstoreOutlined,
  CodeOutlined,
  DesktopOutlined,
  MoonOutlined,
  SettingOutlined,
  SunOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { StatusTable } from "./status";
import { stages } from "./stages";
import "./styles.css";

const { Header, Content, Footer } = Layout;

const filterOptions = [
  { label: "All", value: "all", icon: <AppstoreOutlined /> },
  { label: "BE", value: "BE", icon: <CodeOutlined /> },
  { label: "FE", value: "FE", icon: <DesktopOutlined /> },
  { label: "SP", value: "SP", icon: <ToolOutlined /> },
];

const stageOptions = [
  { key: "development", label: "Development" },
  { key: "testing", label: "Testing" },
  { key: "production", label: "Production" },
];

const readStoredValue = (key, fallback) => {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

const writeStoredValue = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // The UI should remain usable when browser storage is unavailable.
  }
};

const readVisibleStages = () => {
  try {
    const storedStages = JSON.parse(
      window.localStorage.getItem("visibleStages") || "null",
    );

    if (
      Array.isArray(storedStages) &&
      storedStages.every((stage) => stages.includes(stage))
    ) {
      return storedStages;
    }
  } catch {
    // Use all stages when browser storage contains invalid data.
  }

  return stages;
};

const App = () => {
  const [serviceFilter, setServiceFilter] = React.useState(() =>
    readStoredValue("serviceFilter", "all"),
  );
  const [themeMode, setThemeMode] = React.useState(() =>
    readStoredValue("themeMode", "light"),
  );
  const [serviceSearch, setServiceSearch] = React.useState(() =>
    readStoredValue("serviceSearch", ""),
  );
  const [visibleStages, setVisibleStages] = React.useState(readVisibleStages);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [debouncedServiceSearch, setDebouncedServiceSearch] =
    React.useState(serviceSearch);
  const isDark = themeMode === "dark";

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedServiceSearch(serviceSearch);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [serviceSearch]);
  const changeServiceFilter = (value) => {
    setServiceFilter(value);
    writeStoredValue("serviceFilter", value);
  };
  const changeThemeMode = (value) => {
    setThemeMode(value);
    writeStoredValue("themeMode", value);
  };
  const changeServiceSearch = (event) => {
    const { value } = event.target;
    setServiceSearch(value);
    writeStoredValue("serviceSearch", value);
  };
  const toggleStage = (stage) => {
    setVisibleStages((currentStages) => {
      const nextStages = currentStages.includes(stage)
        ? currentStages.filter((currentStage) => currentStage !== stage)
        : [...currentStages, stage];
      writeStoredValue("visibleStages", JSON.stringify(nextStages));
      return nextStages;
    });
  };
  const settingsItems = [
    isDark
      ? {
          key: "light",
          icon: <SunOutlined />,
          label: "Switch to day theme",
        }
      : {
          key: "dark",
          icon: <MoonOutlined />,
          label: "Switch to night theme",
        },
  ];

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
        token: { colorPrimary: "#002d60" },
      }}
    >
      <div className={`app ${isDark ? "app--dark" : ""}`}>
        <Layout className="layout">
          <Header className="app-header">
            <h1 className="app-title">service monitor</h1>
            <div className="app-controls">
              <div className="service-search">
                <Input
                  value={serviceSearch}
                  onChange={changeServiceSearch}
                  placeholder="Search services"
                  allowClear
                  aria-label="Search services"
                />
              </div>
              <Segmented
                options={filterOptions}
                value={serviceFilter}
                onChange={changeServiceFilter}
                aria-label="Filter services by type"
              />
              <Dropdown
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                popupRender={() => (
                  <div
                    className={`settings-menu ${
                      isDark ? "settings-menu--dark" : ""
                    }`}
                  >
                    <button
                      className="settings-theme-item"
                      type="button"
                      onClick={() => {
                        changeThemeMode(isDark ? "light" : "dark");
                        setSettingsOpen(false);
                      }}
                    >
                      {settingsItems[0].icon}
                      <span>{settingsItems[0].label}</span>
                    </button>
                    <Divider />
                    {stageOptions.map(({ key, label }) => (
                      <div className="settings-stage-item" key={key}>
                        <Checkbox
                          checked={visibleStages.includes(key)}
                          onChange={() => toggleStage(key)}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {label}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                )}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Tooltip title="Settings">
                  <Button
                    type="text"
                    aria-label="Settings"
                    icon={<SettingOutlined />}
                    className="settings-button"
                  />
                </Tooltip>
              </Dropdown>
            </div>
          </Header>
          <Content className="app-content">
            <div className="app-content-inner">
              <StatusTable
                serviceFilter={serviceFilter}
                serviceSearch={debouncedServiceSearch}
                visibleStages={visibleStages}
              />
            </div>
          </Content>
          <Footer className="app-footer">Akelius service monitor v1.0.2</Footer>
        </Layout>
      </div>
    </ConfigProvider>
  );
};

createRoot(document.getElementById("root")).render(<App />);

// Hot Module Replacement
if (module.hot) {
  module.hot.accept();
}
